/**
 * Convert any dashboard spec the backend serves — v1, v2 or already-v3 — into
 * one v3 shape, in memory, before it reaches the renderer.
 *
 * WHY normalize on read: the backend serves `dashboard_version.spec` RAW (no
 * on-read conversion), and the DB rewrite is a manual per-environment script
 * (`scripts/migrate_dashboard_specs_v3.py --apply`), not a deploy-time
 * migration. Until that script runs in an env, that env serves a MIX of v2 and
 * v3 specs, for an unknown window. Normalizing at every entry point lets the
 * renderer keep a single v3-only path instead of branching per widget, and makes
 * the script's timing a non-event for the webapp.
 *
 * This is a step-for-step port of `app/services/dashboard_spec_v3_migrate.py`
 * (`migrate_spec_v2_to_v3` / `needs_v3_migration`) on `iq-backend/iq-dashboards`.
 * The parity fixtures in `__fixtures__/` are generated by running that Python on
 * the `.v2.json` inputs, so a divergence here fails
 * `dashboardSpecV3Normalize.test.ts` rather than surfacing as a rendering
 * difference between a migrated and an unmigrated dashboard. Keep the two in
 * step: when the backend migrator changes, re-generate the fixtures and port the
 * change.
 */

import * as Sentry from "@sentry/react";
import type {
  DashboardSpec,
  DashboardSpecV3,
  Filter,
  Section,
  UIAction,
  UIStateDefaults,
} from "@/types/dashboard.types";

/** Ui-state paths are built as plain strings here and typed at the boundary. */
type Defaults = Record<string, boolean>;

type Json = Record<string, unknown>;

/** Element kinds that can carry a v2 `visibleWhen`, and their path prefix. */
type ElementKind = "viz" | "kpi" | "section" | "callout";

function isPlainObject(v: unknown): v is Json {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/** Only dicts are walked, mirroring the Python's `isinstance(x, dict)` guards. */
function objectsIn(list: unknown): Json[] {
  return Array.isArray(list) ? list.filter(isPlainObject) : [];
}

function isFilterCondition(cond: unknown): cond is { filterId: string; equals: unknown } {
  return isPlainObject(cond) && "filterId" in cond && "equals" in cond;
}

function isContextCondition(cond: unknown): cond is { contextKey: string; equals: string } {
  return isPlainObject(cond) && "contextKey" in cond && "equals" in cond;
}

/**
 * An element's id as the Python reads it: `x.get("id") or ""`. A FALSY id (0, "",
 * false) becomes `""`, which `compileVisibleWhen` treats as "no id" and skips —
 * `String(id ?? "")` would instead compile a `viz.0.visible` path the backend
 * migrator never writes.
 */
function idOf(value: unknown): string {
  return value ? String(value) : "";
}

/** `_ensure_list`: coerce `obj[key]` to an array in place and return it. */
function ensureList(obj: Json, key: string): unknown[] {
  const val = obj[key];
  if (!Array.isArray(val)) obj[key] = [];
  return obj[key] as unknown[];
}

/**
 * `_find_filter`: the filter with this id, wherever it is declared — dashboard
 * level, a tab, a tab's section, or a dashboard-level section.
 */
function findFilter(spec: Json, filterId: string): Json | null {
  const match = (list: unknown) => objectsIn(list).find((f) => f.id === filterId) ?? null;

  const top = match(spec.filters);
  if (top) return top;
  for (const tab of objectsIn(spec.tabs)) {
    const inTab = match(tab.filters);
    if (inTab) return inTab;
    for (const sec of objectsIn(tab.sections)) {
      const inSec = match(sec.filters);
      if (inSec) return inSec;
    }
  }
  for (const sec of objectsIn(spec.sections)) {
    const inSec = match(sec.filters);
    if (inSec) return inSec;
  }
  return null;
}

/**
 * `_all_filter_ids`: every filter id in the spec, in declaration order, deduped.
 * This is what a missing `filterIds` expands to — v1/v2 read "no filterIds" as
 * "consumes ALL filters", and v3 requires the array to be explicit.
 */
function allFilterIds(spec: Json): string[] {
  const ids: string[] = [];
  const seen = new Set<string>();
  const add = (fid: unknown) => {
    if (typeof fid === "string" && fid && !seen.has(fid)) {
      seen.add(fid);
      ids.push(fid);
    }
  };
  const addAll = (list: unknown) => objectsIn(list).forEach((f) => add(f.id));

  addAll(spec.filters);
  for (const tab of objectsIn(spec.tabs)) {
    addAll(tab.filters);
    for (const sec of objectsIn(tab.sections)) addAll(sec.filters);
  }
  for (const sec of objectsIn(spec.sections)) addAll(sec.filters);
  return ids;
}

/**
 * Does a filter's `defaultValue` satisfy `equals`, with v2's `isVisibleWhen`
 * semantics? A multiselect default holds when the value is IN it; a scalar
 * default holds on equality. A filter with no default holds nothing.
 */
function filterDefaultHolds(filter: Json, equals: string): boolean {
  const def = filter.defaultValue;
  if (def === undefined) return false;
  if (Array.isArray(def)) return def.includes(equals);
  return def === equals;
}

/**
 * `_compile_visible_when`: turn one element's v2 `visibleWhen` into v3 state.
 *
 * - context-driven → the condition can't change while the dashboard is on
 *   screen, so it is BAKED into a static default from `spec.context`.
 * - filter-driven → the default is EVALUATED against the gating filter's
 *   `defaultValue` (the value the filter is seeded with at mount), plus a paired
 *   show/hide `change` action on that filter so flipping it reveals/hides the
 *   element as before.
 *
 * Evaluating the default matters because `change` actions only fire from
 * `setFilter`/reset — never for the values `initialFilterValues` seeds at mount.
 * A hard `false` default therefore left an element whose condition ALREADY holds
 * at load hidden until the user toggled the filter away and back.
 *
 * `findFilter` returns the FIRST filter with that id in spec order (dashboard
 * level, then each tab, then sections), so a dashboard that declares the same
 * filter id in two tabs with different defaults resolves the default — and the
 * paired actions — against the first declaration, not the active tab's.
 *
 * The key is dropped either way (including for a malformed condition, and for an
 * element with no id) — a normalized spec never carries `visibleWhen`.
 */
function compileVisibleWhen(
  element: Json,
  kind: ElementKind,
  elementId: string,
  spec: Json,
  defaults: Defaults,
): void {
  const cond = element.visibleWhen;
  delete element.visibleWhen;
  if (!cond || !elementId) return;

  const path = `${kind}.${elementId}.visible`;

  if (isContextCondition(cond)) {
    const ctx = isPlainObject(spec.context) ? spec.context : {};
    // Python compares `ctx.get(key) == equals`, so a MISSING key (None) matches an
    // `equals: null`. Both sides are coalesced to null to keep that outcome —
    // a strict `===` on `undefined` vs `null` would diverge from the fixtures.
    defaults[path] = (ctx[cond.contextKey] ?? null) === (cond.equals ?? null);
    return;
  }
  if (!isFilterCondition(cond)) return;

  // Start hidden; resolved below from the gating filter's own `defaultValue`.
  defaults[path] = false;
  const filter = findFilter(spec, cond.filterId);
  // A dangling `filterId` leaves the element hidden and nothing to reveal it —
  // same fail-closed outcome v2's `isVisibleWhen` gave an unknown filter id.
  if (!filter) return;

  // The mount-time answer: the filter is seeded with its `defaultValue`, and no
  // `change` action fires for that seeding, so the default must already be right.
  // Only a STRING `equals` counts: `matchesWhen` honours nothing else, so a
  // non-string one can never be re-hidden by the paired actions — leave it hidden
  // rather than show an element the filter bar has no way to take back.
  defaults[path] = typeof cond.equals === "string" && filterDefaultHolds(filter, cond.equals);

  const actions = ensureList(filter, "on");
  actions.push({ event: "change", when: { equals: cond.equals }, set: { [path]: true } });
  actions.push({ event: "change", when: { notEquals: cond.equals }, set: { [path]: false } });
}

/** `_ensure_filter_ids`: fill a missing/null `filterIds` with every filter id. */
function ensureFilterIds(widget: Json, fallbackIds: string[]): void {
  if (!("filterIds" in widget) || widget.filterIds == null) {
    widget.filterIds = [...fallbackIds];
  }
}

/** `_union_filter_ids_from_widgets`: null when any widget still means "all". */
function unionFilterIds(widgets: Json[]): Set<string> | null {
  const union = new Set<string>();
  for (const w of widgets) {
    const fids = w.filterIds;
    if (fids == null) return null;
    if (Array.isArray(fids)) fids.forEach((id) => typeof id === "string" && union.add(id));
  }
  return union;
}

/**
 * `_count_section_tab_strips`: how many sections carry a tab strip — dashboard-level
 * sections AND sections nested inside a dashboard tab. Scoping (below) only kicks in
 * above one, so a single-strip dashboard keeps the historical full map.
 */
function countSectionTabStrips(spec: Json): number {
  let count = 0;
  const check = (sec: Json) => {
    if (objectsIn(sec.tabs).length) count += 1;
  };
  for (const sec of objectsIn(spec.sections)) check(sec);
  for (const tab of objectsIn(spec.tabs)) {
    for (const sec of objectsIn(tab.sections)) check(sec);
  }
  return count;
}

/**
 * `_strip_scope_ids`: the filter ids ONE section's own tab strip consumes, sorted.
 * Anything outside this set belongs to another strip (or to nothing), and this strip
 * must not write it.
 */
function stripScopeIds(sectionTabs: [Json, Json[]][], fids: string[]): string[] {
  const known = new Set(fids);
  const scope = new Set<string>();
  for (const [, vizs] of sectionTabs) {
    for (const viz of vizs) {
      if (!Array.isArray(viz.filterIds)) continue;
      for (const id of viz.filterIds) {
        if (typeof id === "string" && known.has(id)) scope.add(id);
      }
    }
  }
  return [...scope].sort();
}

/** `_collect_section_widgets`: a section's KPIs, visualizations and tab charts. */
function collectSectionWidgets(section: Json): Json[] {
  const out: Json[] = [
    ...objectsIn(section.kpis),
    ...objectsIn(section.visualizations),
  ];
  for (const tab of objectsIn(section.tabs)) {
    const content = isPlainObject(tab.content) ? tab.content : {};
    out.push(...objectsIn(content.visualizations));
  }
  return out;
}

/**
 * `_synthesize_tab_filter_visibility`: v2 dimmed the filter bar automatically
 * from the mounted widgets' `filterIds`; v3 has no built-in dimming, so the
 * migrator writes that behaviour out as an explicit `select` action per tab —
 * filters the tab's widgets consume stay enabled, the rest are disabled. Visibility
 * is left untouched: out-of-scope filters grey out but stay on screen, so authored
 * defaults/actions remain the only thing that can hide a filter.
 *
 * `scopeIds` limits which filter ids this strip writes. Without it (or for a
 * dashboard-level strip) the strip owns EVERY filter id, which is only safe while a
 * single strip exists — two strips writing the same paths overwrite each other.
 */
function synthesizeTabFilterVisibility(
  tab: Json,
  widgets: Json[],
  fids: string[],
  { isSectionTab, scopeIds = null }: { isSectionTab: boolean; scopeIds?: string[] | null },
): void {
  if (!fids.length) return;

  let scope = !isSectionTab || scopeIds === null ? fids : scopeIds;
  const union = unionFilterIds(widgets);
  // A widget with no `filterIds` still means "consumes ALL", so this strip falls back
  // to the full map — a scoped map would disable filters that widget genuinely uses.
  const relevant = union ?? new Set(fids);
  if (union === null) scope = fids;

  const setMap: Defaults = {};
  for (const fid of scope) {
    setMap[`filter.${fid}.disabled`] = !relevant.has(fid);
  }
  // An empty scope means this strip's tabs consume no known filter — write nothing.
  if (!Object.keys(setMap).length) return;

  const actions = ensureList(tab, "on");
  // Already migrated (or hand-authored): don't stack a second dimming action. A
  // `select` that writes a filter path through `unset` counts too — an appended
  // action runs after it, so its `set` would undo the authored reset.
  const writesFilterPath = (a: Json): boolean =>
    (isPlainObject(a.set) && Object.keys(a.set).some((k) => k.startsWith("filter."))) ||
    (Array.isArray(a.unset) && a.unset.some((p) => typeof p === "string" && p.startsWith("filter.")));
  const alreadySynthesized = actions.some(
    (a) => isPlainObject(a) && a.event === "select" && writesFilterPath(a),
  );
  if (alreadySynthesized) return;

  actions.push({ event: "select", set: setMap });
}

/** `_walk_has_key`: does `key` appear anywhere in the tree? */
function walkHasKey(node: unknown, key: string): boolean {
  if (isPlainObject(node)) {
    if (key in node) return true;
    return Object.values(node).some((v) => walkHasKey(v, key));
  }
  if (Array.isArray(node)) return node.some((item) => walkHasKey(item, key));
  return false;
}

/**
 * Whether this spec still has to be converted — the FE mirror of the backend's
 * `needs_v3_migration`. A spec that already reads `schema_version: "v3"` and
 * carries no `context` / `visibleWhen` is left EXACTLY as served, by reference.
 */
export function needsV3Normalization(spec: unknown): boolean {
  if (!isPlainObject(spec)) return true;
  const version = String(spec.schema_version ?? "").trim().toLowerCase();
  if (version !== "v3") return true;
  if ("context" in spec) return true;
  return walkHasKey(spec, "visibleWhen");
}

/**
 * What the BACKEND served, read off the raw spec before any normalization: its own
 * `schema_version`, or `"v2"` when it carries none (every pre-migration spec).
 *
 * Normalize-on-read means the renderer and the Debug JSON inspector always show
 * v3, so this is the only way to tell whether an env's DB has actually run the
 * migration. Deliberately a separate read rather than anything the normalizer
 * emits — its output is a byte-for-byte parity contract with the backend migrator.
 */
export function servedSchemaVersion(spec: unknown): string {
  if (!isPlainObject(spec)) return "v2";
  const version = String(spec.schema_version ?? "").trim();
  return version || "v2";
}

/**
 * Return this spec as v3. Already-v3 specs pass through UNTOUCHED (same
 * reference, so react-query's caches and the preview's memoized widgets don't
 * see a new object); anything else is deep-copied and converted, leaving the
 * caller's input untouched.
 *
 * Idempotent: `normalizeSpecToV3(normalizeSpecToV3(s))` returns the first
 * result's own reference.
 *
 * @throws TypeError when `spec` is not an object — the caller (see the API entry
 * points) reports it and falls back to the dashboard error state rather than
 * rendering a half-normalized spec.
 */
export function normalizeSpecToV3(spec: unknown): DashboardSpecV3 {
  if (!needsV3Normalization(spec)) return spec as DashboardSpecV3;
  if (!isPlainObject(spec)) {
    throw new TypeError(`dashboard spec must be an object, got ${spec === null ? "null" : typeof spec}`);
  }

  const out = structuredClone(spec) as Json;
  // Always normalize the stamp, and drop the legacy `version` key so nothing
  // downstream confuses the SCHEMA version with the DOCUMENT version.
  out.schema_version = "v3";
  delete out.version;

  const uiState = isPlainObject(out.uiState) ? out.uiState : null;
  const defaults: Defaults =
    uiState && isPlainObject(uiState.defaults) ? { ...(uiState.defaults as Defaults) } : {};

  const fids = allFilterIds(out);
  const multiStrip = countSectionTabStrips(out) > 1;

  const walkSection = (section: Json): void => {
    compileVisibleWhen(section, "section", idOf(section.id), out, defaults);
    for (const callout of objectsIn(section.callouts)) {
      compileVisibleWhen(callout, "callout", idOf(callout.id), out, defaults);
    }
    for (const kpi of objectsIn(section.kpis)) {
      ensureFilterIds(kpi, fids);
      compileVisibleWhen(kpi, "kpi", idOf(kpi.id), out, defaults);
    }
    for (const viz of objectsIn(section.visualizations)) {
      ensureFilterIds(viz, fids);
      compileVisibleWhen(viz, "viz", idOf(viz.id), out, defaults);
    }
    // First pass fills every tab chart's `filterIds` — the scope below is read off
    // them, so it can only be computed once the whole strip has been walked.
    const sectionTabs: [Json, Json[]][] = [];
    for (const tab of objectsIn(section.tabs)) {
      const content = isPlainObject(tab.content) ? tab.content : {};
      const vizs = objectsIn(content.visualizations);
      for (const viz of vizs) {
        ensureFilterIds(viz, fids);
        compileVisibleWhen(viz, "viz", idOf(viz.id), out, defaults);
      }
      sectionTabs.push([tab, vizs]);
    }

    // Scope this strip to the filter ids its OWN tabs use, so two strips in one
    // dashboard don't overwrite each other's filter-bar state. A lone strip keeps the
    // historical full map (it greys filters nothing on the page consumes).
    const scopeIds = multiStrip ? stripScopeIds(sectionTabs, fids) : null;

    for (const [tab, vizs] of sectionTabs) {
      synthesizeTabFilterVisibility(tab, vizs, fids, { isSectionTab: true, scopeIds });
    }
  };

  for (const kpi of objectsIn(out.kpis)) {
    ensureFilterIds(kpi, fids);
    compileVisibleWhen(kpi, "kpi", idOf(kpi.id), out, defaults);
  }

  for (const section of objectsIn(out.sections)) walkSection(section);

  for (const tab of objectsIn(out.tabs)) {
    for (const kpi of objectsIn(tab.kpis)) {
      ensureFilterIds(kpi, fids);
      compileVisibleWhen(kpi, "kpi", idOf(kpi.id), out, defaults);
    }
    const tabWidgets: Json[] = [];
    for (const section of objectsIn(tab.sections)) {
      walkSection(section);
      tabWidgets.push(...collectSectionWidgets(section));
    }
    tabWidgets.push(...objectsIn(tab.kpis));
    synthesizeTabFilterVisibility(tab, tabWidgets, fids, { isSectionTab: false });
  }

  // Context is consumed by the baking above; a v3 spec has no runtime context.
  delete out.context;

  if (Object.keys(defaults).length) {
    const existingUi = isPlainObject(out.uiState) ? out.uiState : null;
    const existingDefaults =
      existingUi && isPlainObject(existingUi.defaults) ? (existingUi.defaults as Defaults) : {};
    // Compiled defaults win over anything already authored for the same path.
    const merged = { ...existingDefaults, ...defaults };
    if (existingUi) existingUi.defaults = merged;
    else out.uiState = { defaults: merged };
  }

  return out as unknown as DashboardSpecV3;
}

// ---------------------------------------------------------------------------
// Guarded entry points
//
// Every place a spec enters the app goes through one of these two, so a
// normalizer bug is OBSERVED (Sentry) instead of silently changing what a
// dashboard looks like, and a half-normalized spec is never rendered — the
// normalizer works on a deep copy, so a throw leaves the input untouched.
// ---------------------------------------------------------------------------

interface NormalizeMeta {
  /** Where the spec came from, e.g. `"getDashboard"` — the Sentry tag. */
  source: string;
  dashboardId?: string;
}

function captureNormalizeFailure(error: unknown, { source, dashboardId }: NormalizeMeta): void {
  Sentry.captureException(error instanceof Error ? error : new Error(String(error)), {
    tags: {
      area: "dashboard_spec_v3_normalize",
      source,
      ...(dashboardId != null && { dashboard_id: String(dashboardId) }),
    },
  });
}

/**
 * Normalize a fetched spec, or FAIL the fetch. Reports to Sentry and rethrows, so
 * the caller's react-query state goes to `error` and the view shows its standard
 * "couldn't load this dashboard" panel — never a partially-converted spec.
 */
export function normalizeSpecOrThrow(spec: unknown, meta: NormalizeMeta): DashboardSpecV3 {
  try {
    return normalizeSpecToV3(spec);
  } catch (error) {
    captureNormalizeFailure(error, meta);
    throw error;
  }
}

/**
 * Normalize a STREAMED draft frame. Reports to Sentry and returns the frame
 * as-served on failure: the create-flow preview is a live draft the agent is
 * still writing, and blanking it out mid-conversation costs the user more than
 * one frame rendering in its pre-normalization shape.
 */
export function normalizeSpecSafe(spec: DashboardSpec, meta: NormalizeMeta): DashboardSpec {
  try {
    return normalizeSpecToV3(spec);
  } catch (error) {
    captureNormalizeFailure(error, meta);
    return spec;
  }
}

// ---------------------------------------------------------------------------
// Typed accessors — the renderer reads a normalized spec through these so the
// `UIStatePath` template-literal type is applied once, here, instead of at every
// call site (the JSON itself is only `Record<string, boolean>`).
// ---------------------------------------------------------------------------

/** The starting ui-state of a normalized spec (`{}` when it declares none). */
export function specUiDefaults(spec?: DashboardSpec | null): UIStateDefaults {
  return spec?.uiState?.defaults ?? {};
}

/** Every filter that writes into one view's shared values map, by id. */
export function filterActionsById(filters?: Filter[], sections?: Section[]): Map<string, UIAction[]> {
  const out = new Map<string, UIAction[]>();
  const add = (list?: Filter[]) => {
    for (const f of list ?? []) {
      if (f?.id && f.on?.length && !out.has(f.id)) out.set(f.id, f.on);
    }
  };
  add(filters);
  for (const s of sections ?? []) add(s?.filters);
  return out;
}
